import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

const SharePost = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [selectedPostId, setSelectedPostId] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await api.get("/posts");
        setPosts(response.data?.data || response.data || []);
      } catch (err) {
        setError("Impossible de charger les posts.");
      } finally {
        setLoadingPosts(false);
      }
    };
    fetchPosts();
  }, []);

  const selectedPost = useMemo(() =>
    posts.find((p) => String(p.id) === String(selectedPostId)) || null,
    [posts, selectedPostId]
  );

  const generatedSubject = useMemo(() =>
    selectedPost ? `Nouveau post en ligne : ${selectedPost.title}` : "",
    [selectedPost]
  );

  const generatedMessage = useMemo(() =>
    selectedPost?.description || "Un nouvel article est disponible sur le site.",
    [selectedPost]
  );

  const finalSubject = customSubject.trim() || generatedSubject;
  const finalMessage = customMessage.trim() || generatedMessage;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPostId) { setError("Veuillez sélectionner un post."); return; }
    try {
      setSending(true);
      setError("");
      setSuccess("");
      await api.post("/newsletter/send-post", {
        postId: selectedPostId,
        subject: finalSubject,
        message: finalMessage,
      });
      setSuccess("Le mail de partage du post a bien été envoyé aux abonnés actifs.");
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto p-6">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-amber-700 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-amber-100">
              Newsletter · Partage d'article
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">Partager un nouveau post</h1>
            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Sélectionnez un article et préparez un envoi destiné aux abonnés actifs.
            </p>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <section className="xl:col-span-2 rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <div className="mb-6 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-white">Préparer l'envoi</h2>
                <p className="mt-1 text-sm text-slate-400">Choisissez un post et personnalisez le message si besoin.</p>
              </div>
              <button
                onClick={() => navigate("/dashboard/newsletter")}
                className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-800"
              >
                Retour
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Choisir un post</label>
                <select
                  value={selectedPostId}
                  onChange={(e) => { setSelectedPostId(e.target.value); setError(""); setSuccess(""); }}
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 outline-none transition focus:border-amber-500"
                  disabled={loadingPosts}
                >
                  <option value="">{loadingPosts ? "Chargement..." : "Sélectionnez un article"}</option>
                  {posts.map((post) => (
                    <option key={post.id} value={post.id}>{post.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Sujet du mail</label>
                <input
                  type="text"
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  placeholder={generatedSubject || "Le sujet sera généré automatiquement"}
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-amber-500"
                />
                <p className="mt-2 text-xs text-slate-500">Laisse vide pour utiliser le sujet généré automatiquement.</p>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Message d'introduction</label>
                <textarea
                  rows={6}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder={generatedMessage || "Le contenu sera généré automatiquement"}
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-amber-500"
                />
                <p className="mt-2 text-xs text-slate-500">Laisse vide pour utiliser la description du post.</p>
              </div>

              {error && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
              )}
              {success && (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">{success}</div>
              )}

              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={sending || loadingPosts}
                  className="rounded-xl bg-amber-500 px-5 py-3 font-semibold text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
                >
                  {sending ? "Envoi en cours..." : "Envoyer aux abonnés"}
                </button>
                <button
                  type="button"
                  onClick={() => { setSelectedPostId(""); setCustomSubject(""); setCustomMessage(""); setError(""); setSuccess(""); }}
                  className="rounded-xl border border-slate-700 px-5 py-3 font-semibold text-slate-200 transition hover:bg-slate-800"
                >
                  Réinitialiser
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Aperçu de l'envoi</h2>
            {!selectedPost ? (
              <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/60 p-5 text-sm leading-6 text-slate-400">
                Sélectionnez un post pour voir un aperçu.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-400">Sujet</p>
                  <p className="text-sm font-semibold text-white">{finalSubject}</p>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-400">Post sélectionné</p>
                  <p className="text-base font-semibold text-white">{selectedPost.title}</p>
                  {selectedPost.slug && (
                    <p className="mt-1 text-xs text-slate-500">Slug : /posts/{selectedPost.slug}</p>
                  )}
                  {selectedPost.description && (
                    <p className="mt-3 text-sm leading-6 text-slate-400">{selectedPost.description}</p>
                  )}
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-400">Message</p>
                  <p className="whitespace-pre-line text-sm leading-6 text-slate-300">{finalMessage}</p>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default SharePost;